import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ApsPermitedScreen } from '../../models/aps-permission/aps-permited-screen';
import { ConstantsFordProtect } from '../../models/constants/constants-ford-protect';
import { ContractManual } from '../../models/contract-manual/contract-manual';
import { SalesTypeFord } from '../../models/contract/sales-type-ford';
import { OperationCode } from '../../models/enum/operation-code';
import { SalesTypeFordEnum } from '../../models/enum/sales-type-ford';
import { CoverageCategory } from '../../models/product/coverage-category';
import { ProductActive } from '../../models/product/product-active';
import { ProductFull } from '../../models/product/product-full';
import { VinValidate } from '../../models/vin-validate/vin-validate';
import { VinSaveAndVector } from '../../models/vin/vin-save-and-vector';
import { VinValidation } from '../../models/vin/vin-validation';
import { ApsPermissionService } from '../../services/aps-permission/aps-permission.service';
import { ContractManualService } from '../../services/contract-manual/contract-manual.service';
import { ContractService } from '../../services/contract/contract.service';
import { CoverageService } from '../../services/coverage/coverage.service';
import { ProductService } from '../../services/product/product.service';
import { VinValidateService } from '../../services/vin-validate-service/vin-validate-service.service';

type ProductChangeEvent = { value: { id: number } };
type FormattedContractManual = Omit<ContractManual, 'saleDate' | 'startDate' | 'endDate' | 'cvvgDate'> & {
	saleDate: string | null;
	startDate: string | null;
	endDate: string | null;
	cvvgDate: string | null;
};

@Component({
	selector: 'app-contract-manual',
	templateUrl: './contract-manual.component.html',
	styleUrls: ['./contract-manual.component.scss'],
	standalone: false
})
export class ContractManualComponent implements OnInit {
	private readonly destroyRef = inject(DestroyRef);

	pt = {
		firstDayOfWeek: 0,
		dayNames: ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'],
		dayNamesShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'],
		dayNamesMin: ['Do', 'Se', 'Te', 'Qu', 'Qu', 'Se', 'Sa'],
		monthNames: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho',
			'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
		monthNamesShort: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
		today: 'Hoje',
		clear: 'Limpar'
	};

	es = {
		firstDayOfWeek: 0,
		dayNames: ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sábado'],
		dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'],
		dayNamesMin: ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'],
		monthNames: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio',
			'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
		monthNamesShort: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
		today: 'Hoy',
		clear: 'Límpar'
	};

	calendarLang = this.pt;

	countries = [
		{ label: 'TRANSLATE.CHOOSE.COUNTRY.ARG', value: ConstantsFordProtect.ARG },
		{ label: 'TRANSLATE.CHOOSE.COUNTRY.BRA', value: ConstantsFordProtect.BRA },
		{ label: 'TRANSLATE.CHOOSE.COUNTRY.CHL', value: ConstantsFordProtect.CHL },
		{ label: 'TRANSLATE.CHOOSE.COUNTRY.COL', value: ConstantsFordProtect.COL },
		{ label: 'TRANSLATE.CHOOSE.COUNTRY.VEN', value: ConstantsFordProtect.VEN }
	];

	contractGesb = [
		{ label: 'TRANSLATE.NOT', value: false },
		{ label: 'TRANSLATE.YES', value: true }
	];

	salesTypeFords: SalesTypeFord[] = [];
	salesTypeFord: SalesTypeFord = new SalesTypeFord();

	country = ConstantsFordProtect.BRA;
	isContractGesb = false;

	productActiveCountries: ProductActive[] = [];
	productActive: ProductActive = new ProductActive();

	coverageCategorys: CoverageCategory[] = [];
	coverageCategory: CoverageCategory;

	vins = '';
	vinSaveAndVectors: VinSaveAndVector[] = [];
	contractsManual: ContractManual[] = [];
	duplicateVinsFromInput: string[] = [];
	duplicateVinsInputMessage = '';
	invalidLengthVinsFromInput: string[] = [];
	invalidLengthVinsInputMessage = '';
	notFoundVinsFromValidation: string[] = [];
	notFoundVinsValidationMessage = '';

	errorsVins: string[] = [];
	hasErrors = false;
	contractErrors = false;
	description: string = '';
	blockedUi = false;
	isValidatingVins = false;
	isCreatingContracts = false;

	constructor(private translateService: TranslateService,
		private messageService: MessageService,
		private apsPermissionService: ApsPermissionService,
		private contractService: ContractService,
		private productService: ProductService,
		private contractManualService: ContractManualService,
		private vinValidateService: VinValidateService,
		private coverageService: CoverageService,
	) {
		this.coverageCategory = new CoverageCategory();
		this.salesTypeFord = new SalesTypeFord();
	}

	ngOnInit() {

		this.apsPermissionService.permissionValidation(ApsPermitedScreen.screenManualContract);
		this.searchProductActiveCountry();

		this.updateCalendarLanguage(this.translateService.currentLang || this.translateService.defaultLang || ConstantsFordProtect.PT_BR);

		this.translateService.onLangChange
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((event: LangChangeEvent) => {
				this.updateCalendarLanguage(event.lang);
			});

	}

	private updateCalendarLanguage(language: string): void {
		this.calendarLang = language.toLowerCase().startsWith('es') ? this.es : this.pt;
		this.searchSalesTypeFord();
	}

	private searchSalesTypeFord() {

		this.contractService.searchSalesTypeFord()
			.subscribe({
				next: (response: SalesTypeFord[]) => {

					this.salesTypeFords = response;
					this.translateSalesTypeFords(this.salesTypeFords);

					this.salesTypeFords = response;
					if (this.salesTypeFords.length > 0) {
						this.salesTypeFord = this.salesTypeFords[0];
					}
				},
				error: (error) => {

					switch (error.status) {

						case 401:
							this.messageService.add({
								severity: 'error',
								summary: this.translateService.instant('TRANSLATE.ERROR'),
								detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
							});
							break;

						case 400:
						case 404:
						case 500:
							this.messageService.add({
								severity: 'error',
								summary: this.translateService.instant('TRANSLATE.ERROR'),
								detail: this.translateService.instant('TRANSLATE.SALE.TYPE.NOT.FOUND')
							});
							break;

						default:
							this.messageService.add({
								severity: 'error',
								summary: this.translateService.instant('TRANSLATE.ERROR'),
								detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
							});
							break;

					}
				}
			});

	}

	private translateSalesTypeFords(response: SalesTypeFord[]): SalesTypeFord[] {

		for (const item of response) {

			switch (item.description) {
				case SalesTypeFordEnum.RETAIL_DESCRIPTION:
					item.description = this.translateService.instant('TRANSLATE.RETAIL');
					break;
				case SalesTypeFordEnum.DIRECT_DESCRIPTION:
					item.description = this.translateService.instant('TRANSLATE.DIRECT');
					break;
				case SalesTypeFordEnum.GOVERNMENT_DESCRIPTION:
					item.description = this.translateService.instant('TRANSLATE.FLEET');
					break;
				case SalesTypeFordEnum.FREE_CONTRACT_DESCRIPTION:
					item.description = this.translateService.instant('TRANSLATE.FREE.CONTRACT');
					break;
				case SalesTypeFordEnum.FORD_FLEET_DESCRIPTION:
					item.description = this.translateService.instant('TRANSLATE.INTERNAL.FLEET');
					break;
				case SalesTypeFordEnum.FORD_GO_DESCRIPTION:
					item.description = this.translateService.instant('TRANSLATE.INTERNAL.FORDGO');
					break;
				case SalesTypeFordEnum.TELEMARKETING_DESCRIPTION:
					item.description = this.translateService.instant('TRANSLATE.INTERNAL.TELEMARKETING');
					break;
			}
		}
		return response;
	}

	private searchProductActiveCountry() {

		this.productService.getActivesProductsAndNotRestrictedByCountry(this.country)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response: ProductActive[]) => {

				this.productActiveCountries = response;

				if (this.productActiveCountries.length > 0) {
					this.productActive = this.productActiveCountries[0];
					this.searchCoverageCategoryAndProductId(this.productActive.id);
				} else {
					this.productActive = new ProductActive();
				}

			}, (error) => {

				switch (error.status) {

					case 401:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
						});
						break;

					case 400:
					case 404:
					case 500:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.PRODUCTS.NOT.FOUND')
						});
						break;

					default:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
						});
						break;
				}
			});
	}

	formatDescription(description: string): string {

		return description.length > 50 ? description.substring(0, 50) + '...' : description;
	}

	private searchCoverageCategoryAndProductId(productId: number) {

		this.coverageService.getSearchCoverageCategoryAndProductId(productId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response: CoverageCategory[]) => {

				this.coverageCategorys = response;

				if (this.coverageCategorys.length > 0) {
					this.coverageCategory = this.coverageCategorys[0];
					this.description = this.coverageCategory.description.length > 50 ? this.coverageCategory.description.substring(0, 50) + '...' : this.coverageCategory.description;

				} else {
					this.coverageCategory = new CoverageCategory();
				}
			}, (error) => {

				switch (error.status) {

					case 401:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
						});
						break;

					case 400:
					case 404:
					case 500:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.PRODUCTS.NOT.FOUND')
						});
						break;

					default:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
						});
						break;
				}
			});
	}

	private resetCountryDependentContractWorkflow(): void {
		this.vins = '';
		this.vinSaveAndVectors = [];
		this.contractsManual = [];
		this.duplicateVinsFromInput = [];
		this.duplicateVinsInputMessage = '';
		this.invalidLengthVinsFromInput = [];
		this.invalidLengthVinsInputMessage = '';
		this.notFoundVinsFromValidation = [];
		this.notFoundVinsValidationMessage = '';
		this.errorsVins = [];
		this.hasErrors = false;
		this.contractErrors = false;
	}

	countryChange(_event: unknown) {
		if (this.blockedUi) {
			return;
		}

		this.resetCountryDependentContractWorkflow();
		this.searchProductActiveCountry();
	}

	productChange(event: ProductChangeEvent) {
		this.searchCoverageCategoryAndProductId(event.value.id);
	}

	buttonCleanVins() {
		if (this.blockedUi) {
			return;
		}
		this.vins = '';
		this.duplicateVinsFromInput = [];
		this.duplicateVinsInputMessage = '';
		this.invalidLengthVinsFromInput = [];
		this.invalidLengthVinsInputMessage = '';
		this.notFoundVinsFromValidation = [];
		this.notFoundVinsValidationMessage = '';
		this.errorsVins = [];
		this.hasErrors = false;
	}

	validateVinsButton() {
		if (this.blockedUi) {
			return;
		}

		this.buttonCleanContract();

		if (this.validationComboBox()) {

			this.buttonCleanValidationVins();
			this.blockedUi = true;
			this.isValidatingVins = true;
			const vinValidation: VinValidation = new VinValidation();
			vinValidation.country = this.country;
			vinValidation.vins = this.verificationBreakVin();
			this.vins = vinValidation.vins.join('\n');

			if (this.duplicateVinsFromInput.length > 0) {

				this.duplicateVinsInputMessage = this.getDuplicateVinsInputMessage();
				this.messageService.add({
					severity: 'warn',
					summary: this.translateService.instant('TRANSLATE.ATTENTION'),
					detail: this.duplicateVinsInputMessage
				});
			} else {
				this.duplicateVinsInputMessage = '';
			}

			if (this.invalidLengthVinsFromInput.length > 0) {

				this.invalidLengthVinsInputMessage = this.getInvalidLengthVinsInputMessage();
				this.messageService.add({
					severity: 'warn',
					summary: this.translateService.instant('TRANSLATE.ATTENTION'),
					detail: this.invalidLengthVinsInputMessage
				});
			} else {
				this.invalidLengthVinsInputMessage = '';
			}

			this.searchValidationVinsSaveAndVector(vinValidation);
		}
	}

	buttonRemoveVin(vinSaveAndVector: VinSaveAndVector) {

		const vinToRemove = this.normalizeVin(vinSaveAndVector.vin);
		this.vinSaveAndVectors = this.vinSaveAndVectors.filter(element => this.normalizeVin(element.vin) !== vinToRemove);
	}

	private searchValidationVinsSaveAndVector(vinValidation: VinValidation) {

		if (!vinValidation.vins || vinValidation.vins.length === 0) {
			this.blockedUi = false;
			this.isValidatingVins = false;
			return;
		}

		const notFoundVins = new Set<string>();
		let pendingRequests = vinValidation.vins.length;
		let ignoredDuplicateCount = 0;
		const finalizeValidationRequest = () => {

			pendingRequests -= 1;
			if (pendingRequests <= 0) {
				this.notFoundVinsFromValidation = Array.from(notFoundVins);

				if (this.notFoundVinsFromValidation.length > 0) {
					this.vins = vinValidation.vins
						.filter(vin => !notFoundVins.has(vin))
						.join('\n');

					this.notFoundVinsValidationMessage = this.getNotFoundVinsValidationMessage();
					this.messageService.add({
						severity: 'warn',
						summary: this.translateService.instant('TRANSLATE.ATTENTION'),
						detail: this.notFoundVinsValidationMessage
					});
				} else {
					this.notFoundVinsValidationMessage = '';
				}

				if (ignoredDuplicateCount > 0) {

					const duplicateValidationMessage = this.getDuplicateVinsIgnoredValidationMessage(ignoredDuplicateCount);
					this.messageService.add({
						severity: 'warn',
						summary: this.translateService.instant('TRANSLATE.ATTENTION'),
						detail: duplicateValidationMessage
					});
				}

				this.blockedUi = false;
				this.isValidatingVins = false;
			}
		};

		vinValidation.vins.forEach(vin => {

			this.vinValidateService.getVinValidate(vinValidation.country, vin, ConstantsFordProtect.PT_BR)
				.subscribe({
					next: (response: VinValidate) => {

						const vinSaveAndVector: VinSaveAndVector = new VinSaveAndVector();
						vinSaveAndVector.country = vinValidation.country;
						vinSaveAndVector.modelYear = response.modelYear;
						vinSaveAndVector.operation = OperationCode.CAR;
						vinSaveAndVector.status = ConstantsFordProtect.CONTRACT_ATIVO_NAME;
						vinSaveAndVector.tma = response.tma;
						vinSaveAndVector.vin = this.normalizeVin(response.vin);
						vinSaveAndVector.warrantyStartDate = response.warrantyStartDate;
						vinSaveAndVector.wersCode = response.vehicleLine;
						vinSaveAndVector.modelYear = response.modelYear;
						vinSaveAndVector.vehicleLine = response.vehicleLine;
						vinSaveAndVector.version = response.version;
						vinSaveAndVector.productId = this.productActive.id;
						vinSaveAndVector.productName = this.productActive.name;
						vinSaveAndVector.coverageId = this.coverageCategory.id;
						vinSaveAndVector.coverageName = this.coverageCategory.name;

						const normalizedResponseVin = this.normalizeVin(vinSaveAndVector.vin);
						const alreadyExists = this.vinSaveAndVectors.some(item => this.normalizeVin(item.vin) === normalizedResponseVin);
						if (!alreadyExists) {
							this.vinSaveAndVectors.push(vinSaveAndVector);
						} else {
							ignoredDuplicateCount += 1;
						}
						finalizeValidationRequest();
					},
					error: (error) => {

						switch (error.status) {

							case 401:
								this.messageService.add({
									severity: 'error',
									summary: this.translateService.instant('TRANSLATE.ERROR'),
									detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
								});
								break;

							case 400:
							case 404:
								notFoundVins.add(vin);
								break;

							case 500:
								this.messageService.add({
									severity: 'error',
									summary: this.translateService.instant('TRANSLATE.ERROR'),
									detail: this.translateService.instant('TRANSLATE.ERROR.VALIDATION.OF.VINS.NOT.FOUND')
								});
								break;

							default:
								this.messageService.add({
									severity: 'error',
									summary: this.translateService.instant('TRANSLATE.ERROR'),
									detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
								});
								break;
						}

						finalizeValidationRequest();
					}
				});
		});

	}

	private verificationBreakVin(): string[] {

		if (this.breakVinsSplitEnter().length > 0) {
			return this.normalizeVinList(this.breakVinsSplitEnter());
		}

		if (this.breakVinsSplitVirgulas().length > 0) {
			return this.normalizeVinList(this.breakVinsSplitVirgulas());
		}

		return [];
	}

	private breakVinsSplitEnter(): string[] {
		return this.vins.split('\n');
	}

	private breakVinsSplitVirgulas(): string[] {
		return this.vins.split(',');
	}

	private isExistCaracterBreakSplit(): boolean {

		const resultVigula = this.vins.includes(',');
		if (resultVigula) {
			return true;
		} else {
			return false;
		}
	}

	private validationComboBox() {

		let validation = true;
		if (this.salesTypeFord !== null && this.salesTypeFord.id > 0) {
			if (this.productActive !== null && this.productActive.id > 0) {
				if (this.coverageCategory !== null && this.coverageCategory.id > 0) {
					const normalizedVins = this.verificationBreakVin();

					if (this.isExistCaracterBreakSplit()) {
						this.duplicateVinsFromInput = [];
						this.duplicateVinsInputMessage = '';

						validation = false;
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.ERROR.VINS.FIELD.WITH.INVALID.SEPARATOR')
						});

					} else if (!normalizedVins || normalizedVins.length === 0) {
						this.duplicateVinsFromInput = [];
						this.duplicateVinsInputMessage = '';

						validation = false;
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.ERROR.VINS.FIELD.EMPTY')
						});
					}
				} else {

					validation = false;
					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.ERROR.PLEASE.SELECT.COVERAGE')
					});
				}
			} else {

				validation = false;
				this.messageService.add({
					severity: 'error',
					summary: this.translateService.instant('TRANSLATE.ERROR'),
					detail: this.translateService.instant('TRANSLATE.ERROR.PLEASE.SELECT.PRODUCT')
				});
			}
		} else {

			validation = false;
			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.ERROR.PLEASE.SELECT.SALE.TYPE')
			});
		}

		return validation;
	}

	buttonCleanValidationVins() {
		if (this.blockedUi) {
			return;
		}
		this.vinSaveAndVectors = [];
	}

	buttonValidationContractVins() {

		if (this.blockedUi) {
			return;
		}

		// Validate that there are validated VINs in the table
		if (this.vinSaveAndVectors.length === 0) {

			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.ERROR.NO.VALIDATED.VINS.TO.ADD')
			});
			return;
		}

		if (this.validationComboBox()) {

			const existingVins = new Set(this.contractsManual.map(contract => this.normalizeVin(contract.vin)));

			const vinsToAdd = this.vinSaveAndVectors.filter(vinItem =>
				vinItem.status === null || vinItem.status.match('VIN.ERROR.SAVE_VECTOR') === null
			);

			const uniqueVinsToAdd: VinSaveAndVector[] = [];
			let skippedDuplicateCount = 0;

			vinsToAdd.forEach(vinItem => {
				const normalizedVin = this.normalizeVin(vinItem.vin);

				if (existingVins.has(normalizedVin)) {
					skippedDuplicateCount += 1;
					return;
				}

				existingVins.add(normalizedVin);
				uniqueVinsToAdd.push(vinItem);
			});

			const addedVinsCount = uniqueVinsToAdd.length;

			uniqueVinsToAdd.forEach(vinItem => {
				this.contractsManual.push(this.createContractManual(vinItem));
			});

			this.vinSaveAndVectors = this.vinSaveAndVectors.filter(vinItem =>
				!(vinItem.status === null || vinItem.status.match('VIN.ERROR.SAVE_VECTOR') === null)
			);

			if (addedVinsCount > 0) {
				this.messageService.add({
					severity: 'success',
					summary: this.translateService.instant('TRANSLATE.SUCCESS'),
					detail: this.translateService.instant('TRANSLATE.SUCCESS.VINS.ADDED.CONTRACT.LIST', { count: addedVinsCount })
				});
			}

			if (skippedDuplicateCount > 0) {
				this.messageService.add({
					severity: 'warn',
					summary: this.translateService.instant('TRANSLATE.ATTENTION'),
					detail: this.translateService.instant('TRANSLATE.WARNING.DUPLICATE.VINS.IGNORED', { count: skippedDuplicateCount })
				});
			}

		}
	}

	private normalizeVin(vin: string): string {

		if (!vin) {
			return '';
		}

		return vin.trim().toUpperCase();
	}

	private normalizeVinList(vins: string[]): string[] {

		const uniqueVins = new Set<string>();
		const duplicateVins = new Set<string>();
		const invalidLengthVins = new Set<string>();

		vins.forEach(vin => {
			const normalizedVin = this.normalizeVin(vin);
			if (normalizedVin) {
				if (normalizedVin.length > 17) {
					invalidLengthVins.add(normalizedVin);
					return;
				}

				if (uniqueVins.has(normalizedVin)) {
					duplicateVins.add(normalizedVin);
					return;
				}

				uniqueVins.add(normalizedVin);
			}
		});

		this.duplicateVinsFromInput = Array.from(duplicateVins);
		this.invalidLengthVinsFromInput = Array.from(invalidLengthVins);

		return Array.from(uniqueVins);
	}

	private translateWithFallback(key: string, params: Record<string, unknown>, fallback: string): string {

		const translated = this.translateService.instant(key, params);
		if (!translated || translated === key) {
			return fallback;
		}

		return translated;
	}

	private getDuplicateVinsInputMessage(): string {

		const count = this.duplicateVinsFromInput.length;
		const vins = this.duplicateVinsFromInput.join(', ');
		const isSpanish = this.translateService.currentLang?.startsWith('es');
		const fallback = isSpanish
			? `${count} VIN(s) duplicado(s) fueron removido(s) automáticamente: ${vins}`
			: `${count} VIN(s) duplicado(s) foram removido(s) automaticamente: ${vins}`;

		return this.translateWithFallback('TRANSLATE.WARNING.DUPLICATE.VINS.INPUT', { count, vins }, fallback);
	}

	private getInvalidLengthVinsInputMessage(): string {

		const count = this.invalidLengthVinsFromInput.length;
		const vins = this.invalidLengthVinsFromInput.join(', ');
		const isSpanish = this.translateService.currentLang?.startsWith('es');
		const fallback = isSpanish
			? `${count} VIN(s) inválido(s) con más de 17 caracteres fue(ron) removido(s) automáticamente: ${vins}`
			: `${count} VIN(s) inválido(s) com mais de 17 caracteres foram removido(s) automaticamente: ${vins}`;

		return this.translateWithFallback('TRANSLATE.WARNING.INVALID.LENGTH.VINS.INPUT', { count, vins }, fallback);
	}

	private getNotFoundVinsValidationMessage(): string {

		const count = this.notFoundVinsFromValidation.length;
		const vins = this.notFoundVinsFromValidation.join(', ');
		const isSpanish = this.translateService.currentLang?.startsWith('es');
		const fallback = isSpanish
			? `${count} VIN(s) no válido(s) o inexistente(s) en la base fue(ron) removido(s) de la validación: ${vins}`
			: `${count} VIN(s) inválido(s) ou inexistente(s) na base foram removido(s) da validação: ${vins}`;

		return this.translateWithFallback('TRANSLATE.WARNING.NOT.FOUND.VINS.VALIDATION', { count, vins }, fallback);
	}

	private getDuplicateVinsIgnoredValidationMessage(count: number): string {

		const isSpanish = this.translateService.currentLang?.startsWith('es');
		const fallback = isSpanish
			? `${count} VIN(s) duplicado(s) fue(ron) ignorado(s) durante la validación.`
			: `${count} VIN(s) duplicado(s) foram ignorado(s) na validação.`;

		return this.translateWithFallback('TRANSLATE.WARNING.DUPLICATE.VINS.IGNORED.VALIDATION', { count }, fallback);
	}

	// Serviço responsável por extrair apenas o nome do produto do rótulo completo, removendo o código e a cobertura.
	private extractProductNameOnly(productLabel: string | null | undefined): string {
		const normalizedLabel = (productLabel || '').trim();
		if (!normalizedLabel) {
			return '';
		}

		const labelParts = normalizedLabel.split(' - ');
		if (labelParts.length >= 3) {
			return labelParts.slice(1, -1).join(' - ').trim();
		}

		if (labelParts.length === 2) {
			return labelParts[1].trim();
		}

		return normalizedLabel;
	}

	private createContractManual(vinSaveAndVector: VinSaveAndVector): ContractManual {

		const contractManual: ContractManual = new ContractManual();
		contractManual.hasError = false;
		contractManual.country = vinSaveAndVector.country;
		contractManual.vin = vinSaveAndVector.vin;
		contractManual.cvvgDate = vinSaveAndVector.warrantyStartDate;
		contractManual.startDate = vinSaveAndVector.warrantyStartDate;
		contractManual.endDate = new Date();
		contractManual.saleType = this.salesTypeFord.id;
		contractManual.isGlobalContract = this.isContractGesb;
		contractManual.user = this.apsPermissionService.getUserCsid();
		contractManual.status = ConstantsFordProtect.CONTRACT_ATIVO_CODE;
		contractManual.tmaCode = vinSaveAndVector.tma;
		contractManual.vehicleLineCode = vinSaveAndVector.vehicleLine;
		contractManual.modelYear = vinSaveAndVector.modelYear;

		const productFull: ProductFull = new ProductFull();
		productFull.productId = vinSaveAndVector.productId;
		productFull.productName = this.extractProductNameOnly(vinSaveAndVector.productName);
		productFull.coverageId = vinSaveAndVector.coverageId;
		productFull.coverageName = vinSaveAndVector.coverageName;
		productFull.priceBookId = 9999999999;
		productFull.productPolicyId = 9999999999;

		contractManual.product = productFull;
		return contractManual;
	}

	buttonCleanContract() {

		if (this.blockedUi) {
			return;
		}
		this.contractsManual = [];
	}

	buttonRemoveVinContract(contractManual: ContractManual) {

		const vinToRemove = this.normalizeVin(contractManual.vin);
		this.contractsManual = this.contractsManual.filter(element => this.normalizeVin(element.vin) !== vinToRemove);
	}

	buttonCreateContract() {
		if (this.blockedUi) {
			return;
		}

		const contractsToCreate = this.getUniqueContractsForCreation(this.contractsManual);

		// Validate that contracts list is not empty
		if (contractsToCreate.length === 0) {
			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.CONTRACTS.LIST.REQUIRED')
			});
			return;
		}

		// Format date fields to yyyy-MM-dd before sending to backend
		const formattedContractsToCreate = this.formatContractDates(contractsToCreate);

		this.blockedUi = true;
		this.isCreatingContracts = true;

		this.contractManualService.createContractManual(formattedContractsToCreate)
			.subscribe({
				next: (response: ContractManual[]) => {
					this.contractsManual = response;

					this.contractsManual.forEach(contractManual => {
						if (contractManual.hasError === true) {
							this.errorsVins.push(contractManual.vin);
							this.hasErrors = true;
							return;
						}
					});

					if (this.hasErrors === true) {

						this.messageService.add({
							severity: 'warn',
							summary: this.translateService.instant('TRANSLATE.ATTENTION'),
							detail: this.translateService.instant('TRANSLATE.ATTENTION.CHECK.STATUS.EACH.CHASSIS')
						});
					} else {

						this.messageService.add({
							severity: 'success',
							summary: this.translateService.instant('TRANSLATE.SUCCESS'),
							detail: this.translateService.instant('TRANSLATE.CONTRACT.SUCCESSFULLY.CREATED')
						});
					}

					this.blockedUi = false;
					this.isCreatingContracts = false;

				},
				error: (error) => {
					const backendErrorDetail = this.getBackendErrorDetail(error);

					switch (error.status) {

						case 401:
							this.messageService.add({
								severity: 'error',
								summary: this.translateService.instant('TRANSLATE.ERROR'),
								detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
							});
							break;

						case 400:
						case 404:
						case 500:
							this.messageService.add({
								severity: 'error',
								summary: this.translateService.instant('TRANSLATE.ERROR'),
								detail: backendErrorDetail || this.translateService.instant('TRANSLATE.ERROR.WHEN.CREATING.MANUAL.CONTRACTS')
							});
							break;

						default:
							this.messageService.add({
								severity: 'error',
								summary: this.translateService.instant('TRANSLATE.ERROR'),
								detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
							});
							break;
					}

					this.blockedUi = false;
					this.isCreatingContracts = false;
				}
			});
	}

	private getUniqueContractsForCreation(contracts: ContractManual[]): ContractManual[] {

		const seenVins = new Set<string>();
		const uniqueContracts: ContractManual[] = [];

		contracts.forEach(contract => {
			const normalizedVin = this.normalizeVin(contract?.vin);
			if (!normalizedVin || seenVins.has(normalizedVin)) {
				return;
			}

			seenVins.add(normalizedVin);
			contract.vin = normalizedVin;
			uniqueContracts.push(contract);
		});

		return uniqueContracts;
	}

	private getBackendErrorDetail(error: unknown): string {

		const errorPayload = (error as { error?: { message?: unknown; detail?: unknown; error_description?: unknown } } | null)?.error;
		const detail = errorPayload?.message || errorPayload?.detail || errorPayload?.error_description;
		return typeof detail === 'string' ? detail : '';
	}

	private formatDateToYyyyMmDd(date: Date | string | null | undefined): string | null {
		if (!date) {
			return null;
		}

		let dateObj: Date;
		if (typeof date === 'string') {
			dateObj = new Date(date);
		} else {
			dateObj = date;
		}

		// Check if date is valid
		if (isNaN(dateObj.getTime())) {
			return null;
		}

		const year = dateObj.getFullYear();
		const month = String(dateObj.getMonth() + 1).padStart(2, '0');
		const day = String(dateObj.getDate()).padStart(2, '0');

		return `${year}-${month}-${day}`;
	}

	private formatContractDates(contracts: ContractManual[]): FormattedContractManual[] {
		return contracts.map(contract => ({
			...contract,
			saleDate: this.formatDateToYyyyMmDd(contract.saleDate),
			startDate: this.formatDateToYyyyMmDd(contract.startDate),
			endDate: this.formatDateToYyyyMmDd(contract.endDate),
			cvvgDate: this.formatDateToYyyyMmDd(contract.cvvgDate)
		}));
	}

}